import React, { useEffect, useState } from "react";
import { getStorage } from "../utils/storage";
import { useNavigate } from "react-router-dom";
import { Page, Button } from "react-onsenui";
import { axiosInstance as axios } from "../utils/axios";
import { getOrgIdsParam } from "../utils/orgSelection";
import { makeDate_Std } from "../utils/date-helper";
import {
    Box,
    Chip,
    CircularProgress,
    Divider,
    ListItemButton,
    Paper,
    SwipeableDrawer,
    Typography,
} from '@mui/material';
import { Icon } from "@iconify/react";
import { clPageStyle, clContentBox, clCardSx, clCardListSx, clCentredState, CL_PRIMARY, ClPageHeader, ClPageTitle } from "./cl-styles";
import { paths } from "../App";
import { buildStandaloneHtmlForPdf } from "../utils/checklistPdf";

/**
 * List API returns a combined `name` only (no nested location/blueprint):
 *   "{template}[ - {role}] - {location}" truncated to 106 chars + " - {D MMM YY hh:mmA}"
 * Location itself can contain hyphens ("Washroom Males - T30", "Washroom-12").
 * Detail drawer loads relations separately — prefer those when present.
 */
const parseInstanceDisplayFields = (data) => {
    const fromRelation = {
        location: data?.location?.location || null,
        template: data?.blueprint?.name || null,
    };
    if (fromRelation.location && fromRelation.template) return fromRelation;

    const raw = String(data?.name ?? '').trim();
    if (!raw) return { location: fromRelation.location, template: fromRelation.template || '—' };

    // Strip trailing " - 27 Mar 26 01:08PM" / " - 5 Jun 26 9:42AM" (spacing/AM-PM variants)
    const withoutDate = raw
        .replace(/\s+-\s+\d{1,2}\s+[A-Za-z]{3}\s+\d{2}\s+\d{1,2}:\d{2}\s*[AaPp][Mm]\s*$/, '')
        .trim();

    // First segment = template; everything after the first " - " = location
    // (location may itself contain " - "; role-in-name cases put role into location text — rare)
    const sep = withoutDate.search(/\s+-\s+/);
    if (sep === -1) {
        return {
            location: fromRelation.location,
            template: fromRelation.template || withoutDate || raw,
        };
    }
    const template = withoutDate.slice(0, sep).trim();
    const location = withoutDate.slice(sep).replace(/^\s+-\s+/, '').trim();
    return {
        location: fromRelation.location || location || null,
        template: fromRelation.template || template || raw,
    };
};

export const ChecklistMyInstances = (props) => {

    const [authUser, setAuthUser] = useState(getStorage('u4'));
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(10)
    const [disableLoadMore, setDisableLoadMore] = useState(false)
    const loadMore = (e) => {
        setPageNum(pageNum + 1);
    }

    /* Navigation */
    let navigate = useNavigate();
    const goBack = () => navigate(paths.checklist.dash, { replace: true });

    const renderListHeader = () => <></>;
    const renderListFooter = () => <></>;

    const [myInstances, setMyInstances] = useState(null);
    const getMyClInstances = async () => {
        if (!authUser || !authUser?.id) return;
        let r = await axios.post("/checklist/my/instances", {
            per_page: pageSize,
            page: pageNum,
            org_ids: getOrgIdsParam(authUser),
        });
        if (r && r.data) {
            setMyInstances(prev => prev ? prev.concat(r.data?.data) : r.data?.data);
            if (r.data.next_page_url === null) {
                setDisableLoadMore(true);
            }
        }
    }

    useEffect(() => {
        getMyClInstances().then();
    }, [pageNum]);


    const [clickedRow, setClickedRow] = useState(0);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const openDrawer = (data) => {
        setClickedRow(data);
        setIsDrawerOpen(true);
    }
    const onDrawerClose = () => {
        setIsDrawerOpen(false);
        setClickedRow(0);
    }

    return (<>
        <Page renderToolbar={() => <ClPageHeader onBack={goBack} />} style={clPageStyle}>
            <Box sx={clContentBox}>
                <ClPageTitle title="Completed Checklists" subtitle="Tap any row to view details" />

                {/* Loading / empty */}
                {!myInstances && (
                    <Box sx={clCentredState}>
                        <CircularProgress size={36} sx={{ color: CL_PRIMARY }} />
                    </Box>
                )}
                {myInstances && !myInstances.length && (
                    <Box sx={clCentredState}>
                        <Typography color="text.secondary">None found</Typography>
                    </Box>
                )}

                {/* List */}
                {myInstances && myInstances.length > 0 && (
                    <Box>
                        <Box sx={clCardListSx}>
                            {myInstances.map((data, i) => {
                                const { location: parsedLocation, template: parsedTemplate } = parseInstanceDisplayFields(data);
                                return (
                                <Paper key={i} elevation={0} sx={clCardSx}>
                                    <ListItemButton onClick={() => openDrawer(data)} sx={{ px: 2, py: 1.25, alignItems: 'flex-start', gap: 1 }}>
                                        {/* Content */}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>

                                            {/* Location */}
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.5 }}>
                                                <Icon icon="mdi:map-marker" style={{ fontSize: 16, color: CL_PRIMARY, flexShrink: 0, marginTop: 2 }} />
                                                <Typography variant="body2" fontWeight={700} sx={{ color: '#1a1a1a', lineHeight: 1.3, fontSize: '0.9rem' }}>
                                                    {parsedLocation || '—'}
                                                </Typography>
                                            </Box>

                                            {/* Time */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                                                <Icon icon="mdi:clock-outline" style={{ fontSize: 15, color: '#888', flexShrink: 0 }} />
                                                <Typography sx={{ color: '#555', fontSize: '0.78rem' }}>
                                                    {makeDate_Std(data.user_actioned_at ?? data.updated_at)}
                                                </Typography>
                                            </Box>

                                            {/* Template */}
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.4 }}>
                                                <Icon icon="mdi:clipboard-text-outline" style={{ fontSize: 15, color: '#888', flexShrink: 0, marginTop: 1 }} />
                                                <Typography sx={{ color: '#444', fontSize: '0.78rem', lineHeight: 1.4 }}>
                                                    <span style={{ fontWeight: 700, color: '#888', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: '0.04em' }}>Template: </span>
                                                    {parsedTemplate}
                                                </Typography>
                                            </Box>

                                            {/* ID */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Icon icon="mdi:pound" style={{ fontSize: 15, color: '#aaa', flexShrink: 0 }} />
                                                <Typography sx={{ color: '#777', fontFamily: 'monospace', fontSize: '0.78rem' }}>
                                                    {data?.id}
                                                </Typography>
                                            </Box>

                                        </Box>

                                        {/* Right column: chip (if any) + PDF */}
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, flexShrink: 0 }}>
                                            {data?.status === 3 && (
                                                <Chip label="Pending Approval" size="small" variant="outlined"
                                                    sx={{ fontSize: '0.65rem', color: '#e65100', borderColor: '#e65100', height: 22 }} />
                                            )}
                                            <DownloadPdfButton instanceId={data?.id} orgId={data?.org_id} />
                                        </Box>
                                    </ListItemButton>
                                </Paper>
                                );
                            })}
                        </Box>

                        {/* Load more */}
                        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
                            <Button style={{ margin: '0 auto', display: 'block' }} onClick={loadMore} disabled={disableLoadMore}>Load More..</Button>
                        </Box>
                    </Box>
                )}

                {!clickedRow ? <></> :
                    <SwipeableDrawer
                        anchor="right"
                        open={isDrawerOpen}
                        onClose={onDrawerClose}
                        onOpen={() => {
                            setIsDrawerOpen(true)
                        }}
                    >
                        <Box sx={{ width: '100vw', maxWidth: 480, display: 'flex', flexDirection: 'column', height: '100%' }}>
                            <RenderClDetails clickedRow={clickedRow} onClose={onDrawerClose} />
                        </Box>
                    </SwipeableDrawer>
                }

            </Box>
        </Page>
    </>);

}

const RenderClDetails = (props) => {
    const delimiter = ";";
    const renderClosingOptions = (closingOptions) => {
        if (!closingOptions) return <></>;
        const closingOptionsArray = closingOptions.split(delimiter);
        return (closingOptionsArray.map((opt, i) => {
            return (<Chip key={i} label={opt} color="primary" variant="outlined" size="small" sx={{ mr: '4px' }} />);
        }));
    }

    const renderItemDetailsCard = (item, index) => {
        return (
            <div style={{
                padding: '10px 12px',
                marginTop: 8,
                border: '1px solid rgba(0,0,0,0.07)',
                borderRadius: 6,
                lineHeight: '1.5'
            }}>
                <p style={{ margin: '0 0 4px', fontWeight: 700, fontSize: '0.85rem', color: '#1a1a1a' }}>{index + 1}. {item.name}</p>
                <p style={{ margin: '0 0 3px', fontSize: '0.8rem' }}><span style={{ color: '#888', fontWeight: 600 }}>Closing: </span><strong>{renderClosingOptions(item?.pivot?.meta?.closing_option)}</strong></p>
                <p style={{ margin: 0, fontSize: '0.8rem' }}><span style={{ color: '#888', fontWeight: 600 }}>Comments: </span>{item?.pivot?.meta?.com ?? <em style={{ color: '#bbb' }}>None</em>}</p>
                {item?.pivot?.meta?.img &&
                    <div style={{ marginTop: 10 }}>
                        <a href={item.pivot.meta.img} target="_blank" rel="noreferrer">
                            <img src={item.pivot.meta.img} alt=""
                                style={{ borderRadius: 1, width: "100%", height: 200, objectFit: 'cover' }} />
                        </a>
                    </div>
                }

            </div>
        )
    }

    const renderClInstanceStatus = (status) => {
        return (<>
            {-1 === status && <b style={{ color: 'darkred' }}>MISSED</b>}
            {0 === status && <b style={{ color: 'goldenrod' }}>PENDING</b>}
            {1 === status && <b style={{ color: 'forestgreen' }}>COMPLETED</b>}
            {3 === status && <b style={{ color: '#e65100' }}>PENDING APPROVAL</b>}
        </>
        );
    }

    const config = {
        itemGrid: props.layout === 'wide' ? 2 : 1,  // pass layout='wide' to render wide version
    }
    const [entityData, setEntityData] = useState({});

    useEffect(() => {
        axios.get(`/checklist/get`, {
            params: {
                id: props.clickedRow.id,
                type: 'instance',
                org_id: props.clickedRow?.org_id,
            }
        }).then(d => {
            setEntityData(d.data)
        });
    }, [props.clickedRow]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Header — matches ClPageHeader style */}
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                px: 1.5, py: 1,
                backgroundColor: '#fff',
                borderBottom: '1px solid rgba(0,0,0,0.07)',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                flexShrink: 0,
            }}>
                <button onClick={props.onClose} style={{ display: 'flex', alignItems: 'center', gap: 2, background: 'none', border: 'none', cursor: 'pointer', padding: '6px 8px', borderRadius: 8 }}>
                    <Icon icon="mdi:chevron-left" style={{ fontSize: 22, color: CL_PRIMARY }} />
                    <span style={{ fontSize: '0.9rem', fontWeight: 600, color: CL_PRIMARY, lineHeight: 1 }}>Back</span>
                </button>
                <Typography variant="caption" sx={{ color: '#777', fontWeight: 500, pr: 1 }}>Checklist Detail</Typography>
            </Box>

            {/* Scrollable content */}
            <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pt: 1.25, pb: 3 }}>

            {(entityData?.status !== undefined && entityData?.status !== null) ?
                <Box sx={{ mb: 1.5, pb: 1.5, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
                    {[
                        { label: 'Location',     value: entityData?.location?.location ?? '—' },
                        { label: 'Template',     value: entityData?.blueprint?.name ?? '—' },
                        { label: 'Created',      value: entityData?.created_at ?? '—' },
                        { label: 'Submitted by', value: entityData?.actioned_by?.name ?? '—' },
                        { label: 'Submitted at', value: entityData?.user_actioned_at ?? entityData?.updated_at ?? '—' },
                    ].map(({ label, value }) => (
                        <Box key={label} sx={{ display: 'flex', gap: 1, py: 0.55, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                            <Typography variant="body2" sx={{ minWidth: 100, fontWeight: 700, color: '#888', flexShrink: 0 }}>{label}</Typography>
                            <Typography variant="body2" sx={{ color: '#333' }}>{value}</Typography>
                        </Box>
                    ))}
                    {/* Status row */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', pt: 0.55 }}>
                        <Typography variant="body2" sx={{ minWidth: 100, fontWeight: 700, color: '#888', flexShrink: 0 }}>Status</Typography>
                        {renderClInstanceStatus(entityData?.status)}
                    </Box>
                </Box>
                : <CircularProgress size={28} sx={{ color: CL_PRIMARY, display: 'block', mx: 'auto', mt: 3 }} />
            }

            {/* No data ? or loading */}
            {!entityData?.items?.length && <div></div>}

            {/* Render items */}
            {
                entityData?.items?.length !== 0 && entityData?.blueprint?.sections?.map((section, indexSection) => {
                    return <div key={indexSection}>
                        {/* Divider */}
                        <Divider sx={{ mt: 2, mb: 1 }} textAlign="center">
                            <span className="checklist-section-divider-tag"
                                style={{
                                    backgroundColor: "#1f1f1f",
                                    color: "white",
                                    padding: '5px 12px',
                                    borderRadius: 4,
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                }}>
                                Section {indexSection + 1}{section.name ? ': ' + section.name : ''}
                            </span>
                        </Divider>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(' + config.itemGrid + ',1fr)',
                            gridGap: 20
                        }}>
                            {
                                entityData?.items.filter((item, indexItem) => (+item?.pivot?.section) === (+section?.id))
                                    .map((item, indexItem) => {
                                        return (<div key={indexItem}>{renderItemDetailsCard(item, indexItem)}</div>)
                                    })
                            }
                        </div>
                    </div>
                })
            }
            </Box>{/* end scrollable content */}
        </Box>
    )
}

/* ─────────────────────────────────────────────────────────────
   DownloadPdfButton
   Sits on each list row. Fetches full instance data on demand,
   builds the standalone HTML page, and writes it into a new tab
   which auto-downloads the PDF and closes itself.
───────────────────────────────────────────────────────────── */
const DownloadPdfButton = ({ instanceId, orgId }) => {
    const [loading, setLoading] = useState(false);

    const handleClick = async (e) => {
        e.stopPropagation(); // don't open the drawer
        if (!instanceId || loading) return;
        setLoading(true);

        // Open the window immediately — synchronously from the user gesture —
        // so browsers don't block it as a popup.
        const newWindow = window.open('', '_blank');
        if (!newWindow) { alert('Please allow popups for this site to download PDFs.'); setLoading(false); return; }

        try {
            const res = await axios.get('/checklist/get', {
                params: { id: instanceId, type: 'instance', org_id: orgId },
            });
            const data = res.data;
            if (!data?.id) { newWindow.close(); return; }

            const safeTs = (data?.user_actioned_at ?? data?.updated_at ?? '')
                .replace(/[:\s/\\]+/g, '-').replace(/[^a-zA-Z0-9\-]/g, '');
            const filename = `Checklist-${data.id}-${safeTs}.pdf`;

            const standaloneHtml = buildStandaloneHtmlForPdf(data, filename, window.location.origin);
            const blob = new Blob([standaloneHtml], { type: 'text/html' });
            const blobUrl = URL.createObjectURL(blob);
            newWindow.location.href = blobUrl;
            // Release the object URL once the new tab has had time to load it
            setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
        } catch (err) {
            console.error('PDF generation failed:', err);
            newWindow.close();
        } finally {
            setLoading(false);
        }
    };

    return (
        <button
            type="button"
            onClick={handleClick}
            disabled={loading}
            title="Download checklist as PDF"
            aria-label="Download checklist as PDF"
            style={{
                flexShrink: 0,
                background: 'none',
                border: '1px solid #1e88e5',
                borderRadius: 6,
                cursor: loading ? 'default' : 'pointer',
                padding: '5px 8px',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                color: '#1e88e5',
                fontSize: '0.75rem',
                fontWeight: 600,
                opacity: loading ? 0.6 : 1,
            }}
        >
            {loading
                ? <CircularProgress size={14} style={{ color: '#1e88e5' }} />
                : <span style={{ fontSize: 14, lineHeight: 1 }}>⬇</span>
            }
            {loading ? 'Generating…' : 'PDF'}
        </button>
    );
}
